// components/OperationalBenefits.jsx
import React from "react";
import { Box, Grid, Typography, Stack } from "@mui/material";
import CtaButton from "../buttonWithArrow";
import Heading from "../heading";

function BulletList({ title, items = [], icons, accent = "#0F7F8A", mb = 4 }) {
    return (
        <Box sx={{ mb: { xs: 3, md: mb } }}>
            {/* Title with underline image */}
            {title && (
                <Typography
                    variant="h6"
                    sx={{
                        fontWeight: 700,
                        display: "inline-block",
                        position: "relative",
                        mb: 1.5,
                        lineHeight: 1.2,
                        // underline image
                        "&::after": {
                            content: '""',
                            position: "absolute",
                            bottom: 0,                 // vertical offset
                            left: "67%",
                            transform: "translateX(-50%) scaleX(1.4)", // center + widen
                            transformOrigin: "center bottom",
                            width: "100%",               // match text width
                            height: 30,                  // thickness
                            backgroundImage: `url(${icons})`,
                            backgroundRepeat: "no-repeat",
                            backgroundSize: "100% 100%",
                            backgroundPosition: "center bottom",
                            pointerEvents: "none",
                            zIndex: 0,
                        },
                    }}
                >
                    {title}
                </Typography>
            )}


            <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0 }}>
                {items.map((text, i) => (
                    <Box
                        key={`${title || "list"}-${i}`}
                        component="li"
                        sx={{ display: "flex", alignItems: "flex-start", mb: 1.25 }}
                    >
                        <Box
                            component="img"
                            src="/img/investments/commercial/arrow.svg"
                            alt={`bullet ${i + 1}`}
                            sx={{
                                width: 28,
                                height: 28,
                                mr: 1.25,
                                mt: "2px",
                                borderRadius: "50%",
                                objectFit: "contain",
                                display: "inline-block",
                            }}
                        />
                        <Typography sx={{ fontSize: 16, lineHeight: 1.5 }}>{text}</Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

export default function OperationalBenefits({ data, onClick }) {
    const {
        subtitle = "Choose which suits you better",
        title = "Operational Benefits",
        accentColor = "#0F7F8A",
        sellers = { title: "For Sellers", items: [] },
        buyers = { title: "For Buyers/Investors", items: [] },
        leaseToOwn,
        strategic = { title: "Strategic Value to RSL", items: [] },
        images = {
            rightTop: null,     // { src, alt }
            leftMiddle: null,   // { src, alt }
            rightBottom: null,  // { src, alt }
        },
        button = { text: "Invest Now", link: "#" },
    } = data;

    // constants (optional, keeps values in one place)
    const DIVIDER_WIDTH = 12;
    const HALF_DIV = 12;


    // Image wrapper with optional accent bar on the left (for top-right image)
    const Img = ({ src, alt, accentLeft = false }) =>
        !src ? null : (
            <Box sx={{ position: "relative" }}>
                {accentLeft && (
                    <Box
                        sx={{
                            position: "absolute",
                            left: -10,                // pulls the bar into the gutter (like the mock)
                            top: 8,
                            bottom: 8,
                            width: 10,
                            borderRadius: 1.5,
                            background: `linear-gradient(180deg, ${accentColor} 0%, ${accentColor}AA 100%)`,
                            boxShadow: "0 6px 20px rgba(15,127,138,.25)",
                            display: { xs: "none", md: "block" },
                        }}
                    />
                )}
                <Box
                    component="img"
                    src={src}
                    alt={alt || ""}
                    sx={{
                        width: "100%",
                        height: { xs: 350, md: 240 },
                        objectFit: "cover",
                        // borderRadius: 2,
                        boxShadow: "0 6px 20px rgba(0,0,0,0.12)",
                        display: "block",
                    }}
                />
            </Box>
        );

    return (
        <Box sx={{ bgcolor: "#f9f9f9" }}>
            <Box
                component="section"
                sx={{
                    py: { xs: 5, md: 4 },
                    maxWidth: { xs: 700, md: 900 },
                    mx: "auto",
                    px: { xs: 2, md: 0 },
                }}
            >
                <Heading title={title} subtitle={subtitle} mb={10} />

                {/* Wrap with relative so we can place a full-height divider */}
                <Box sx={{ position: "relative" }}>
                    {/* Vertical middle divider (md+ only) */}
                    <Box
                        sx={{
                            display: { xs: "none", md: "block" },
                            position: "absolute",
                            top: 0,
                            bottom: 0,
                            left: `calc(50% - ${HALF_DIV}px)`,     // sits exactly on the split
                            width: `${DIVIDER_WIDTH}px`,
                            background: `linear-gradient(180deg, ${accentColor}A6 0%, ${accentColor} 100%)`,
                            pointerEvents: "none",
                            zIndex: 0,
                        }}
                    />


                    <Grid container spacing={3} sx={{ position: "relative", zIndex: 1 }}>
                        {/* Left column */}
                        <Grid item xs={12} md={6} sx={{ paddingTop: "0px  !important" }}>
                            <BulletList title={sellers.title} icons={sellers.icons} items={sellers.items} accent={accentColor} />
                            <Img {...(images.leftMiddle || {})} />
                            <Box sx={{ mt: { xs: 3, md: 4 } }}>
                                <BulletList title={strategic.title} icons={strategic.icons} items={strategic.items} accent={accentColor} />
                            </Box>
                            {
                                images.lastBottom && <Img {...(images.lastBottom || {})} />
                            }
                        </Grid>

                        {/* Right column */}
                        <Grid item xs={12} md={6} sx={{ paddingTop: "0px  !important", paddingLeft: "12px !important" }}>
                            <Img {...(images.rightTop || {})} />
                            <Box sx={{ mt: { xs: 3, md: 4 }, paddingLeft: { md: '12px' } }}>
                                <BulletList title={buyers.title} icons={buyers.icons} items={buyers.items} accent={accentColor} />
                            </Box>
                            <Box sx={{ mt: { xs: 3, md: 4 } }}>
                                <Img {...(images.rightBottom || {})} />
                            </Box>
                            {
                                leaseToOwn && <Box sx={{ mt: { xs: 3, md: 4 }, paddingLeft: { md: '12px' } }}>
                                    <BulletList title={leaseToOwn.title} icons={leaseToOwn.icons} items={leaseToOwn.items} accent={accentColor} mb={0} />
                                </Box>
                            }
                        </Grid>
                    </Grid>
                </Box>

                {button && (
                    <Stack alignItems="center" sx={{ mt: { xs: 4, md: 6 }, mb: { xs: 2, md: 6 } }}>
                        <CtaButton label={button?.text} onClick={() => onClick()} />
                        {/* <CtaButton cta={{ label: button?.text, href: button?.link }} /> */}
                    </Stack>
                )}
            </Box>
        </Box>
    );
}
